import React, { useState } from "react";
import { Table, Tag, Popconfirm } from "antd";
import type { ColumnType } from "antd/es/table";
import { BsDownload } from "react-icons/bs";
import { SlEye } from "react-icons/sl";
import moment from "moment";
import MyPagination from "../../../views/components/Pagination";
import { renderIcon, renderTableText } from "../../../lib/utils";
import { IReportsListResponse } from "../../../types/api/reports";
import { CiEdit } from "react-icons/ci";
import { useCreateReport } from "../../../hooks/api/reports/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

interface ReportsTableProps {
  data?: IReportsListResponse[];
  loading?: boolean;
  DEFAULT_NUQS_CONFIG?: any;
  qData?: any;
  refetch?: any;
}

const DownloadIcon = renderIcon(BsDownload);
const EyeIcon = renderIcon(SlEye);
const EditIcon = renderIcon(CiEdit);

const ReportsTable: React.FC<ReportsTableProps> = ({
  data = [],
  loading = false,
  DEFAULT_NUQS_CONFIG,
  qData,
  refetch,
}) => {
  const message = useMessageApi();

  // ✅ Track current row loading
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  // const handleView = async () => {
  //   const url = window.URL.createObjectURL(
  //     new Blob([htmlContent as any], { type: "text/html" })
  //   );
  //   window.open(url, "_blank");
  // };

  const handleDownload = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "report.pdf";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const { mutate: createReport } = useCreateReport({ qData });

  const handleGeneratePdf = (reportId: string) => {
    setGeneratingId(reportId);

    createReport(
      { reportId },
      {
        onSuccess: () => {
          message.success("PDF generated successfully");
          setGeneratingId(null);
        },
        onError: (err: any) => {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to generate PDF";
          message.error(msg);
          setGeneratingId(null);
        },
      }
    );
  };

  const columns: ColumnType<IReportsListResponse>[] = [
    {
      title: "Report Number",
      dataIndex: "reportNumber",
      key: "reportNumber",
      render: (v) => (
        <Tag color="blue" className="font-medium">
          RPT-{v}
        </Tag>
      ),
    },
    {
      title: "Project Name",
      key: "projectName",
      render: (_, v) =>
        renderTableText(v?.projectDetails?.projectName || "-"),
    },
    {
      title: "Report Type",
      key: "reportType",
      render: (_, v) => (
        <Tag
          color={
            v?.reportType === "FINAL"
              ? "green"
              : v?.reportType === "INITIAL"
              ? "blue"
              : "orange"
          }
          className="font-medium"
        >
          {v?.reportType || "-"}
        </Tag>
      ),
    },
    {
      title: "Created By",
      key: "name",
      render: (_, v) =>
        renderTableText(v?.createdByDetails?.name || "-"),
    },
    {
      title: "Submitted On",
      dataIndex: "submissionDate",
      key: "submissionDate",
      render: (v) =>
        renderTableText(moment(v).format("DD MMM YYYY")),
    },

    // ✅ Generate PDF column
    {
      title: "Generate PDF",
      key: "generatePdf",
      align: "center",
      render: (_, record) => {
        // ❌ Hide for non FINAL
        if (record?.reportType !== "FINAL") return "-";

        const isLoading = generatingId === record?._id;

        return (
          <Popconfirm
            title="Generate PDF"
            description="Do you want to regenerate PDF?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleGeneratePdf(record?._id as string)}
            okButtonProps={{ loading: isLoading }}
            disabled={isLoading}
          >
            <button
              className={`text-primary font-medium ${
                isLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              disabled={isLoading}
            >
              {isLoading ? "Generating..." : "Generate"}
            </button>
          </Popconfirm>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, record) => (
        <div className="flex items-center justify-end gap-3">
          
          {record?.reportType === "FINAL" && (
            <a href={`/issues?projectId=${record?.projectId}`}>
              <EditIcon className="cursor-pointer text-primary text-[22px]" />
            </a>
          )}

          <a
            onClick={() => handleDownload(record?.reportFileUrl || "")}
          >
            <DownloadIcon className="cursor-pointer text-primary text-[18px]" />
          </a>

          <a href={record?.reportFileUrl} target="_blank" rel="noopener noreferrer">
            <EyeIcon className="cursor-pointer text-primary text-[18px]" />
          </a>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Table
        dataSource={data}
        columns={columns}
        rowKey={(item: any) => item._id}
        pagination={false}
        loading={loading}
        scroll={{ x: "max-content" }}
        className="custom-table-class rounded-lg border border-gray-200 shadow-md"
        footer={() => (
          <MyPagination
            qData={qData}
            onChange={refetch}
            total={qData?.total}
            DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
          />
        )}
      />
    </div>
  );
};

export default ReportsTable;